import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Calendar, Download, Save, Clock } from "lucide-react";
import { toast } from "sonner";

export function CustomReportsTab() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    if (!query.trim()) {
      toast.error("Please enter a query");
      return;
    }

    setLoading(true);
    // Simulate AI report generation
    setTimeout(() => {
      setLoading(false);
      toast.success("Report generated successfully!");
    }, 2000);
  };

  const savedTemplates = [
    { name: "Top Performing Jobs", query: "Show me the top 10 performing jobs by revenue last month", lastUsed: "2 days ago" },
    { name: "Lead Conversion Analysis", query: "Analyze lead conversion rates by source for Q1", lastUsed: "1 week ago" },
    { name: "Material Cost Trends", query: "Show material cost trends over the past 6 months", lastUsed: "3 days ago" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Custom AI Reports</h2>
        <p className="text-muted-foreground">Generate custom reports using natural language queries</p>
      </div>

      {/* AI Query Builder */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gradient-to-br from-neon-purple to-neon-pink">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold">AI Report Builder</h3>
          <Badge variant="outline" className="ml-auto bg-neon-purple/10 text-neon-purple">
            Powered by AI
          </Badge>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Ask a question about your data</label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Show me top performing jobs last month"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGenerateReport()}
                className="flex-1"
              />
              <Button onClick={handleGenerateReport} disabled={loading} className="gap-2">
                {loading ? (
                  <>Generating...</>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground mb-2">💡 Example queries:</p>
            <ul className="text-sm space-y-1">
              <li>• "Show me jobs with highest profit margins this quarter"</li>
              <li>• "Compare lead conversion rates by source"</li>
              <li>• "List crew performance metrics for the past 30 days"</li>
              <li>• "Analyze AI confidence scores by damage type"</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Saved Templates */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Saved Report Templates</h3>
        <div className="space-y-3">
          {savedTemplates.map((template, index) => (
            <div key={index} className="flex items-start justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium">{template.name}</p>
                  <Badge variant="outline" className="text-xs">Template</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{template.query}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span>Last used {template.lastUsed}</span>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <Button variant="ghost" size="sm" onClick={() => setQuery(template.query)}>
                  Use
                </Button>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Report Scheduling */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Scheduled Reports</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Automatically generate and email reports on a schedule
        </p>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Calendar className="w-4 h-4" />
            Schedule New Report
          </Button>
          <Button variant="outline" className="gap-2">
            <Save className="w-4 h-4" />
            Save Current Query
          </Button>
        </div>
      </Card>
    </div>
  );
}
